import { useQuery } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { useOTPAuth } from '../auth/providers/OTPAuthProvider';
import { API_URL } from '../utils/config';

// Market Analytics Data Interfaces
export interface MarketPerformanceMetrics {
  adr: number;
  adr_chg_yoy: number;
  occupancy: number;
  occupancy_chg_yoy: number;
  revpar: number;
  revpar_chg_yoy: number;
  revenue: number;
  revenue_chg_yoy: number;
  demand: number;
  demand_chg_yoy: number;
  supply: number;
  supply_chg_yoy: number;
}

export interface MarketPerformanceData {
  current: MarketPerformanceMetrics;
  '3_month': MarketPerformanceMetrics & {
    '3_mo_adr': number;
    '3_mo_adr_chg': number;
    '3_mo_occupancy': number;
    '3_mo_occupancy_chg': number;
    '3_mo_revpar': number;
    '3_mo_revpar_chg': number;
    '3_mo_revenue': number;
    '3_mo_revenue_chg': number;
    '3_mo_demand': number;
    '3_mo_demand_chg': number;
    '3_mo_supply': number;
    '3_mo_supply_chg': number;
  };
  '12_month': MarketPerformanceMetrics & {
    '12_mo_adr': number;
    '12_mo_adr_chg': number;
    '12_mo_occupancy': number;
    '12_mo_occupancy_chg': number;
    '12_mo_revpar': number;
    '12_mo_revpar_chg': number;
    '12_mo_revenue': number;
    '12_mo_revenue_chg': number;
    '12_mo_demand': number;
    '12_mo_demand_chg': number;
    '12_mo_supply': number;
    '12_mo_supply_chg': number;
    '12_mo_average_price_per_building': number;
    '12_mo_delivered_buildings': number;
    '12_mo_delivered_rooms': number;
    '12_mo_opened_buildings': number;
    '12_mo_opened_rooms': number;
    '12_mo_inventory_growth': number;
    '12_mo_sales_volume': number;
    '12_mo_sales_vol_chg': number;
    '12_mo_transactions': number;
    '12_mo_transactions_chg': number;
  };
  ytd: MarketPerformanceMetrics & {
    ytd_adr: number;
    ytd_adr_chg: number;
    ytd_occupancy: number;
    ytd_occupancy_chg: number;
    ytd_revpar: number;
    ytd_revpar_chg: number;
    ytd_revenue: number;
    ytd_revenue_chg: number;
    ytd_demand: number;
    ytd_demand_chg: number;
    ytd_supply: number;
    ytd_supply_chg: number;
  };
  metadata: {
    period: string;
    slice: string;
    forecast_scenario?: string;
    last_processed_month: string;
  };
}

export interface MarketPerformanceByCategory {
  classification: string;
  current: MarketPerformanceMetrics;
  three_month: MarketPerformanceMetrics & {
    adr?: number;
    adr_change?: number;
    demand?: number;
    demand_change?: number;
    occupancy?: number;
    occupancy_change?: number;
    revenue?: number;
    revenue_change?: number;
    revpar?: number;
    revpar_change?: number;
    supply?: number;
    supply_change?: number;
  };
  twelve_month: MarketPerformanceMetrics & {
    adr?: number;
    adr_change?: number;
    demand?: number;
    demand_change?: number;
    occupancy?: number;
    occupancy_change?: number;
    revenue?: number;
    revenue_change?: number;
    revpar?: number;
    revpar_change?: number;
    supply?: number;
    supply_change?: number;
    average_price_per_building?: number;
    delivered_buildings?: number;
    delivered_rooms?: number;
    opened_buildings?: number;
    opened_rooms?: number;
    inventory_growth?: number;
    sales_volume?: number;
    sales_vol_chg?: number;
    transactions?: number;
    transactions_chg?: number;
  };
  ytd: MarketPerformanceMetrics & {
    adr?: number;
    adr_chg_yoy?: number;
    demand?: number;
    demand_chg_yoy?: number;
    occupancy?: number;
    occupancy_chg_yoy?: number;
    revenue?: number;
    revenue_chg_yoy?: number;
    revpar?: number;
    revpar_chg_yoy?: number;
    supply?: number;
    supply_chg_yoy?: number;
  };
  metadata: {
    period: string;
    forecast_scenario?: string;
    last_processed_month: string;
  };
}

export interface TimeSeriesDataPoint {
  date: string;
  slice: string;
  adr?: number;
  adr_change?: number;
  revpar?: number;
  revpar_change?: number;
  occupancy?: number;
  occupancy_change?: number;
  demand?: number;
  demand_change?: number;
  supply?: number;
  supply_change?: number;
  revenue?: number;
  revenue_change?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Hook to fetch market performance data
export const useMarketPerformance = (
  marketId: number | undefined,
  options?: {
    period?: 'current' | '3_month' | '12_month' | 'ytd';
    propertyType?: string;
  }
) => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<MarketPerformanceByCategory[], Error>({
    queryKey: ['marketPerformance', marketId, options],
    queryFn: async (): Promise<MarketPerformanceByCategory[]> => {
      try {
        if (!isAuthenticated || !marketId) {
          return [];
        }

        const params = new URLSearchParams();
        if (options?.period) params.append('period', options.period);
        if (options?.propertyType) params.append('property_type', options.propertyType);

        const url = `${API_URL}/api/market-analytics/markets/${marketId}/performance${
          params.toString() ? `?${params.toString()}` : ''
        }`;
        
        const response = await authenticatedFetch(url, {
          timeout: 30000 // 30 seconds timeout for Market Analytics API
        });
        
        if (!response) {
          console.error('Failed to fetch market performance: No response from authenticatedFetch');
          return [];
        }
        
        if (!response.ok) {
          console.error(`Failed to fetch market performance: HTTP ${response.status} ${response.statusText}`);
          return [];
        }

        const result: ApiResponse<MarketPerformanceByCategory[] | { classifications: MarketPerformanceByCategory[] }> = await response.json();
        
        // Check if the response has the expected structure
        if (result.success && result.data) {
          // If data is directly an array, use it as is
          if (Array.isArray(result.data)) {
            return result.data;
          }
          // If data has classifications property, use that
          if (result.data.classifications) {
            return result.data.classifications;
          }
        }
        
        console.error('Unexpected API response structure:', result);
        return [];
      } catch (error) {
        console.error('Market performance fetch error:', error);
        throw error;
      }
    },
    enabled: isAuthenticated && !!marketId,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
};

// Hook to fetch market time series data
export const useMarketTimeSeries = (
  marketId: number | undefined,
  startDate: string,
  endDate: string,
  options?: {
    metrics?: string[];
    propertyType?: string;
  }
) => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<TimeSeriesDataPoint[], Error>({
    queryKey: ['marketTimeSeries', marketId, startDate, endDate, options],
    queryFn: async (): Promise<TimeSeriesDataPoint[]> => {
      try {
        if (!isAuthenticated || !marketId || !startDate || !endDate) {
          return [];
        }

        const params = new URLSearchParams();
        params.append('start_date', startDate);
        params.append('end_date', endDate);
        if (options?.metrics && options.metrics.length > 0) {
          params.append('metrics', options.metrics.join(','));
        }
        if (options?.propertyType) params.append('property_type', options.propertyType);

        const url = `${API_URL}/api/market-analytics/markets/${marketId}/timeseries?${params.toString()}`;
        
        const response = await authenticatedFetch(url, {
          timeout: 30000 // 30 seconds timeout for Market Analytics API
        });
        
        if (!response) {
          console.error('Failed to fetch market time series: No response from authenticatedFetch');
          return [];
        }
        
        if (!response.ok) {
          console.error(`Failed to fetch market time series: HTTP ${response.status} ${response.statusText}`);
          return [];
        }

        const result: ApiResponse<any> = await response.json();
        
        // Check if we have the expected structure
        if (!result.success || !result.data) {
          console.error('Unexpected time series API response:', result);
          return [];
        }
        
        // Transform the API response to our expected format
        const transformedData: TimeSeriesDataPoint[] = [];
        
        if (result.data.classifications && Array.isArray(result.data.classifications)) {
          result.data.classifications.forEach((classification: any) => {
            const metrics = classification.metrics;
            if (!metrics) return;
            
            // Get all unique dates from all metrics
            const dates = new Set<string>();
            ['adr', 'occupancy', 'revpar'].forEach(metric => {
              if (metrics[metric] && Array.isArray(metrics[metric])) {
                metrics[metric].forEach((item: any) => dates.add(item.date));
              }
            });
            
            // Create data points for each date
            dates.forEach(date => {
              const dataPoint: TimeSeriesDataPoint = {
                date,
                slice: classification.classification,
              };
              
              // Add metric values if they exist
              ['adr', 'occupancy', 'revpar'].forEach(metric => {
                const metricData = metrics[metric]?.find((m: any) => m.date === date);
                if (metricData) {
                  // Convert occupancy from decimal to percentage if needed
                  if (metric === 'occupancy' && metricData.value !== null && metricData.value < 1) {
                    (dataPoint as any)[metric] = metricData.value * 100;
                  } else {
                    (dataPoint as any)[metric] = metricData.value;
                  }
                  (dataPoint as any)[`${metric}_change`] = metricData.change;
                }
              });
              
              transformedData.push(dataPoint);
            });
          });
        }
        
        return transformedData;
      } catch (error) {
        console.error('Market time series fetch error:', error);
        throw error;
      }
    },
    enabled: isAuthenticated && !!marketId && !!startDate && !!endDate,
    staleTime: 10 * 60 * 1000, // Consider data fresh for 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
};

// Hook to fetch supply and demand data
export const useMarketSupplyDemand = (
  marketId: number | undefined,
  period?: 'current' | '3_month' | '12_month' | 'ytd'
) => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<any, Error>({
    queryKey: ['marketSupplyDemand', marketId, period],
    queryFn: async () => {
      try {
        if (!isAuthenticated || !marketId) {
          return null;
        }

        const params = new URLSearchParams();
        if (period) params.append('period', period);

        const url = `${API_URL}/api/market-analytics/markets/${marketId}/supply-demand${
          params.toString() ? `?${params.toString()}` : ''
        }`;
        
        const response = await authenticatedFetch(url, {
          timeout: 30000 // 30 seconds timeout for Market Analytics API
        });
        
        if (!response) {
          console.error('Failed to fetch supply/demand: No response from authenticatedFetch');
          return null;
        }
        
        if (!response.ok) {
          console.error(`Failed to fetch supply/demand: HTTP ${response.status} ${response.statusText}`);
          return null;
        }

        const result: ApiResponse<any> = await response.json();
        return result.data;
      } catch (error) {
        console.error('Supply/demand fetch error:', error);
        throw error;
      }
    },
    enabled: isAuthenticated && !!marketId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

// Hook to compare multiple markets
export const useMarketComparison = (
  marketIds: number[],
  options?: {
    metrics?: string[];
    period?: 'current' | '3_month' | '12_month' | 'ytd';
  }
) => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<any, Error>({
    queryKey: ['marketComparison', marketIds, options],
    queryFn: async () => {
      try {
        if (!isAuthenticated || marketIds.length < 2) {
          return null;
        }

        const params = new URLSearchParams();
        params.append('market_ids', marketIds.join(','));
        if (options?.metrics && options.metrics.length > 0) {
          params.append('metrics', options.metrics.join(','));
        }
        if (options?.period) params.append('period', options.period);

        const url = `${API_URL}/api/market-analytics/markets/compare?${params.toString()}`;
        
        const response = await authenticatedFetch(url, {
          timeout: 30000 // 30 seconds timeout for Market Analytics API
        });
        
        if (!response) {
          console.error('Failed to fetch market comparison: No response from authenticatedFetch');
          return null;
        }
        
        if (!response.ok) {
          console.error(`Failed to fetch market comparison: HTTP ${response.status} ${response.statusText}`);
          return null;
        }

        const result: ApiResponse<any> = await response.json();
        return result.data;
      } catch (error) {
        console.error('Market comparison fetch error:', error);
        throw error;
      }
    },
    enabled: isAuthenticated && marketIds.length >= 2,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};